import React, { useState, useEffect, HTMLAttributes } from "react";

export interface ImageProps
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    "src" | "alt" | "className"
  > {
  src?: string;
  alt?: string;
  className?: string;
  fallbackSrc?: string;
  showAbbreviation?: boolean;
  abbreviationClassName?: string;
}

export const Image: React.FC<ImageProps> = ({
  src = null,
  alt = "",
  className = "",
  fallbackSrc = "",
  showAbbreviation = true,
  abbreviationClassName = "",
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Reset states when src changes
  useEffect(() => {
    setImageSrc(src);
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  // Generate abbreviation from alt text
  const generateAbbreviation = (text: string | undefined): string => {
    if (!text) return "?";

    const words = text.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return words
      .slice(0, 3) // Take first 3 words max
      .map((word: string) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);

    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(false);
      setIsLoading(true);
    }
  };

  // If no src at all, or error with no fallback, show abbreviation
  const shouldShowAbbreviation =
    ((!src && !fallbackSrc) ||
      (hasError && (!fallbackSrc || imageSrc === fallbackSrc))) &&
    showAbbreviation;

  if (shouldShowAbbreviation) {
    const abbreviation = generateAbbreviation(alt);

    return (
      <div
        className={`inline-flex select-none items-center justify-center bg-gray-200 text-sm font-semibold text-gray-600 ${className} ${abbreviationClassName}`}
        title={alt}
        {...(props as HTMLAttributes<HTMLDivElement>)}
      >
        {abbreviation}
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div
          className={`inline-flex animate-pulse items-center justify-center bg-gray-100 text-gray-400 ${className}`}
          {...(props as HTMLAttributes<HTMLDivElement>)}
        >
          <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
      <img
        src={imageSrc ? imageSrc : "null"}
        alt={alt}
        className={`${className} ${isLoading ? "hidden" : ""}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        {...props}
      />
    </>
  );
};
